// src/features/suggestedMentors/suggestedMentorsSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agent } from "../../../utils/agent";
import * as API from "../../../utils/apiPath";

// Thunks
export const loadSuggestedMentors = createAsyncThunk(
    "mentors/loadMentors",
    async ({ request, user_id, role_id, domains, programmes, board_entity_type, board_entity_type_2, years_of_board_experience, objective }, { rejectWithValue }) => {
        try {
            let url =
                API.LOAD_MENTORS +
                "/" +
                user_id +
                "/" +
                role_id +
                "?userDomains=" +
                domains + "&programmes=" + programmes + "&board_entity_type=" + board_entity_type 
                if (years_of_board_experience !== undefined) {
                    url +=
                        "&years_of_board_experience=" +
                        years_of_board_experience + "&objective=" + objective;
                }
            const response = await agent.get(url, {
                params: request,
            });

            const result = response.data?.data;

            return {
                mentors: result?.data || [],
                pagination: result?.pagination || null,
                hasData: result?.data?.length > 0,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const loadRegSuggestedMentors = createAsyncThunk(
    "mentors/loadRegMentors",
    async ({ request, user_id, role_id, domains, programmes, board_entity_type, board_entity_type_2, years_of_board_experience, objective }, { rejectWithValue }) => {
        try {
            let url =
                API.LOAD_REG_MENTORS +
                "/" +
                user_id +
                "/" +
                role_id +
                "?userDomains=" +
                domains + "&programmes=" + programmes + "&board_entity_type=" + board_entity_type 
                if (years_of_board_experience !== undefined) {
                    url +=
                        "&years_of_board_experience=" +
                        years_of_board_experience + "&objective=" + objective;
                }
            const response = await agent.get(url, {
                params: request,
            });

            const result = response.data?.data;

            return {
                mentors: result?.data || [],
                pagination: result?.pagination || null,
                hasData: result?.data?.length > 0,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || error.message);
        }
    }
);

export const saveSelectMentor = createAsyncThunk(
    "suggestedMentors/saveSelectMentor",
    async ({ userId, mentorId }, { rejectWithValue }) => {
        try {
            const res = await agent.post(API.SAVESELECTMENTOR, { userId, mentorId });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const saveWaitlistMentor = createAsyncThunk(
    "suggestedMentors/saveWaitlistMentor",
    async ({ userId, mentorId }, { rejectWithValue }) => {
        try {
            const res = await agent.post(API.SAVEWAITLISTMENTOR, { userId, mentorId });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const getSelectedMentorCount = createAsyncThunk(
    "suggestedMentors/count",
    async (userId, { rejectWithValue }) => {
        try {
            const res = await agent.get(API.GETSELECTEDMENTORCOUNT + "/" + userId);
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const updateUserData = createAsyncThunk(
    "user/updateUserData",
    async (_, { dispatch, rejectWithValue }) => {
        try {
            const response = await agent.get(API.UPDATEUSER);

            if (response.status === 200) {
                // Update the global userInfo state
                dispatch({
                    type: "LOADED_USER_INFO_SUCCESS",
                    userInfo: response.data.data
                });
                return response.data.data; // user data
            }
        } catch (error) {
            return rejectWithValue(error.response?.data || error.message);
        }
    }
);

const menteeDashboardSlice = createSlice({
    name: "menteeDashboard",
    initialState: {
        mentorsData: [],
        pagination: { total_pages: 0, per_page: 10 },
        selectedCount: 0,
        loading: false,
        error: null,
        isProcessing: false, // Full screen loader for mentor actions
        mentorsLoading: false,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // Load Suggested Mentors
            .addCase(loadSuggestedMentors.pending, (state) => {
                state.mentorsLoading = true;   // 👈 START LOADER
            })
            .addCase(loadSuggestedMentors.fulfilled, (state, action) => {
                state.mentorsLoading = false;  // 👈 STOP LOADER
                state.mentorsData = action.payload.mentors;
                state.pagination = action.payload.pagination;
            })
            .addCase(loadSuggestedMentors.rejected, (state, action) => {
                state.mentorsLoading = false;  // 👈 STOP LOADER
                state.error = action.payload;
            })
            .addCase(loadRegSuggestedMentors.pending, (state) => {
                state.mentorsLoading = true;   // 👈 START LOADER
            })
            .addCase(loadRegSuggestedMentors.fulfilled, (state, action) => {
                state.mentorsLoading = false;  // 👈 STOP LOADER
                state.mentorsData = action.payload.mentors;
                state.pagination = action.payload.pagination;
            })
            .addCase(loadRegSuggestedMentors.rejected, (state, action) => {
                state.mentorsLoading = false;  // 👈 STOP LOADER
                state.error = action.payload;
            })

            // Save Selected Mentor
            .addCase(saveSelectMentor.pending, (state) => {
                state.isProcessing = true;
            })
            .addCase(saveSelectMentor.fulfilled, (state, action) => {
                state.selectedCount = action.payload.selected_count || state.selectedCount + 1;
                state.isProcessing = false;
            })
            .addCase(saveSelectMentor.rejected, (state) => {
                state.isProcessing = false;
            })

            // Waitlist
            .addCase(saveWaitlistMentor.pending, (state) => {
                state.isProcessing = true;
            })
            .addCase(saveWaitlistMentor.fulfilled, (state) => {
                // Optionally update mentor status
                state.isProcessing = false;
            })
            .addCase(saveWaitlistMentor.rejected, (state) => {
                state.isProcessing = false;
            })

            // Count
            .addCase(getSelectedMentorCount.fulfilled, (state, action) => {
                state.selectedCount = action.payload;
            })
            .addCase(updateUserData.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateUserData.fulfilled, (state, action) => {
                state.loading = false;
                state.userInfo = action.payload;
            })
            .addCase(updateUserData.rejected, (state) => {
                state.loading = false;
            });
    },
});

export default menteeDashboardSlice.reducer;